import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL;
if (!API_URL) {
    throw new Error('API URL not configured');
}
const ARCA_GREEN = '#6B8E23';

const BulkMessaging = () => {
    const [members, setMembers] = useState([]);
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);

    const [messageData, setMessageData] = useState({
        subject: '',
        content: '',
        attachments: []
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');

    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            setLoadingMembers(true);
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_URL}/members/search?limit=1000000`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                const membersData = response.data.data || [];
                setMembers(membersData);
                console.log(`✅ Loaded ${membersData.length} members`);
                toast.success(`Loaded ${membersData.length} members`);
            }
        } catch (error) {
            console.error('Error fetching members:', error);
            toast.error('Failed to load members. Please try again.');
        } finally {
            setLoadingMembers(false);
        }
    };

    const toggleSelectAll = () => {
        const filtered = getFilteredMembers();
        if (selectAll) {
            setSelectedMemberIds([]);
            setSelectAll(false);
        } else {
            setSelectedMemberIds(filtered.map(m => m.id));
            setSelectAll(true);
        }
    };

    const toggleMemberSelection = (memberId) => {
        setSelectedMemberIds(prev => {
            if (prev.includes(memberId)) {
                const newSelection = prev.filter(id => id !== memberId);
                setSelectAll(false);
                return newSelection;
            } else {
                const newSelection = [...prev, memberId];
                const filtered = getFilteredMembers();
                setSelectAll(newSelection.length === filtered.length);
                return newSelection;
            }
        });
    };

    const getFilteredMembers = () => {
        return members.filter(member => {
            const matchesSearch =
                member.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.email?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesFilter =
                filterType === 'all' ||
                member.membership_type === filterType;

            return matchesSearch && matchesFilter;
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setMessageData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);

        // Limit file size to 10MB per file
        const maxSize = 10 * 1024 * 1024; // 10MB
        const validFiles = [];

        for (const file of files) {
            if (file.size > maxSize) {
                toast.error(`${file.name} is too large. Maximum size is 10MB.`);
                continue;
            }
            validFiles.push(file);
        }

        if (validFiles.length > 0) {
            setMessageData(prev => ({
                ...prev,
                attachments: [...prev.attachments, ...validFiles]
            }));
            toast.success(`${validFiles.length} file(s) added`);
        }

        // Reset input
        e.target.value = '';
    };

    const removeAttachment = (index) => {
        setMessageData(prev => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index)
        }));
        toast.info('File removed');
    };

    const sendBulkMessage = async () => {
        if (selectedMemberIds.length === 0) {
            toast.error('Please select at least one member');
            return;
        }

        if (!messageData.content.trim()) {
            toast.error('Please enter a message');
            return;
        }

        try {
            setSendingMessage(true);
            const token = localStorage.getItem('adminToken');

            const response = await axios.post(
                `${API_URL}/communication/admin/messages/send`,
                {
                    recipient_ids: selectedMemberIds,
                    subject: messageData.subject || 'Message from ARCA Admin',
                    content: messageData.content
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const { successful, failed } = response.data.data;
                if (successful > 0) {
                    toast.success(`Message sent successfully to ${successful} member${successful > 1 ? 's' : ''}!`);
                }
                if (failed > 0) {
                    toast.warning(`Failed to send message to ${failed} member${failed > 1 ? 's' : ''}`);
                }

                // Reset form
                setMessageData({ subject: '', content: '', attachments: [] });
                setSelectedMemberIds([]);
                setSelectAll(false);
            }
        } catch (error) {
            console.error('Error sending messages:', error);
            toast.error(error.response?.data?.message || 'Failed to send messages. Please try again.');
        } finally {
            setSendingMessage(false);
        }
    };

    const filteredMembers = getFilteredMembers();
    const memberTypes = [...new Set(members.map(m => m.membership_type).filter(Boolean))];

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="mb-1" style={{ color: ARCA_GREEN }}>
                        <i className="bi bi-chat-dots-fill me-3"></i>
                        Bulk Messaging
                    </h1>
                    <p className="text-muted mb-0">Send messages to ARCA members</p>
                </div>
                <button
                    className="btn btn-outline-secondary"
                    onClick={fetchMembers}
                    disabled={loadingMembers}
                >
                    <i className={`bi bi-arrow-clockwise me-2 ${loadingMembers ? 'spinner-border spinner-border-sm' : ''}`}></i>
                    {loadingMembers ? 'Loading...' : 'Refresh Members'}
                </button>
            </div>

            <div className="row">
                {/* Left Panel - Member Selection */}
                <div className="col-lg-5">
                    <div className="card shadow-sm">
                        <div className="card-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="bi bi-people-fill me-2"></i>
                                    Select Recipients
                                </h5>
                                {selectedMemberIds.length > 0 && (
                                    <span className="badge bg-light text-dark">
                                        {selectedMemberIds.length} selected
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="card-body">
                            {/* Search and Filter */}
                            <div className="mb-3">
                                <div className="input-group mb-2">
                                    <span className="input-group-text">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by name or email..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <select
                                    className="form-select"
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                >
                                    <option value="all">All Member Types</option>
                                    {memberTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Select All Button */}
                            {!loadingMembers && filteredMembers.length > 0 && (
                                <button
                                    type="button"
                                    className="btn btn-sm w-100 mb-3"
                                    onClick={toggleSelectAll}
                                    style={{
                                        backgroundColor: selectAll ? ARCA_GREEN : '#f8f9fa',
                                        color: selectAll ? 'white' : ARCA_GREEN,
                                        borderColor: ARCA_GREEN
                                    }}
                                >
                                    <i className={`bi ${selectAll ? 'bi-check-square-fill' : 'bi-square'} me-2`}></i>
                                    {selectAll ? 'Deselect All' : 'Select All'} ({filteredMembers.length})
                                </button>
                            )}

                            {/* Member List */}
                            <div
                                style={{
                                    maxHeight: '500px',
                                    overflowY: 'auto',
                                    border: `1px solid ${ARCA_GREEN}40`,
                                    borderRadius: '8px'
                                }}
                            >
                                {loadingMembers ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                            <span className="visually-hidden">Loading members...</span>
                                        </div>
                                        <p className="text-muted mt-2 mb-0">Loading members...</p>
                                    </div>
                                ) : filteredMembers.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="bi bi-inbox display-4 text-muted d-block mb-2"></i>
                                        <p className="text-muted mb-2">
                                            {searchTerm || filterType !== 'all' ? 'No members match your filters' : 'No members found'}
                                        </p>
                                        {(searchTerm || filterType !== 'all') && (
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => {
                                                    setSearchTerm('');
                                                    setFilterType('all');
                                                }}
                                            >
                                                Clear Filters
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="p-2">
                                        {filteredMembers.map(member => (
                                            <div
                                                key={member.id}
                                                className="form-check p-3 mb-2 rounded"
                                                style={{
                                                    backgroundColor: selectedMemberIds.includes(member.id) ? ARCA_GREEN + '15' : '#f8f9fa',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    border: selectedMemberIds.includes(member.id) ? `2px solid ${ARCA_GREEN}` : '1px solid #dee2e6'
                                                }}
                                                onClick={() => toggleMemberSelection(member.id)}
                                            >
                                                <input
                                                    className="form-check-input me-3"
                                                    type="checkbox"
                                                    checked={selectedMemberIds.includes(member.id)}
                                                    onChange={() => toggleMemberSelection(member.id)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <label className="form-check-label w-100" style={{ cursor: 'pointer' }}>
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div className="flex-grow-1">
                                                            <div className="fw-semibold">
                                                                {member.first_name} {member.last_name}
                                                                {member.membership_type && (
                                                                    <span className="badge bg-secondary ms-2 text-xs">
                                                                        {member.membership_type}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-muted small">
                                                                <i className="bi bi-envelope me-1"></i>
                                                                {member.email}
                                                            </div>
                                                            {member.phone && (
                                                                <div className="text-muted small">
                                                                    <i className="bi bi-telephone me-1"></i>
                                                                    {member.phone}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {selectedMemberIds.includes(member.id) && (
                                                            <i className="bi bi-check-circle-fill ms-2" style={{ color: ARCA_GREEN }}></i>
                                                        )}
                                                    </div>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Panel - Message Composer */}
                <div className="col-lg-7">
                    <div className="card shadow-sm">
                        <div className="card-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                            <h5 className="mb-0">
                                <i className="bi bi-pencil-square me-2"></i>
                                Compose Message
                            </h5>
                        </div>
                        <div className="card-body">
                            {/* Subject */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold">
                                    <i className="bi bi-tag me-2" style={{ color: ARCA_GREEN }}></i>
                                    Subject (Optional)
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="subject"
                                    placeholder="Message from ARCA Admin"
                                    value={messageData.subject}
                                    onChange={handleInputChange}
                                    style={{ borderColor: ARCA_GREEN + '80' }}
                                />
                                <small className="form-text text-muted">
                                    Leave blank to use default: "Message from ARCA Admin"
                                </small>
                            </div>

                            {/* Message Content */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold">
                                    <i className="bi bi-chat-text me-2" style={{ color: ARCA_GREEN }}></i>
                                    Message Content *
                                </label>
                                <textarea
                                    className="form-control"
                                    name="content"
                                    rows="12"
                                    placeholder="Type your message here..."
                                    value={messageData.content}
                                    onChange={handleInputChange}
                                    style={{ borderColor: ARCA_GREEN + '80', resize: 'vertical' }}
                                    required
                                ></textarea>
                                <small className="form-text text-muted">
                                    This message will be sent through the ARCA internal messaging system
                                </small>
                            </div>

                            {/* File Attachments */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold">
                                    <i className="bi bi-paperclip me-2" style={{ color: ARCA_GREEN }}></i>
                                    Attachments (Optional)
                                </label>

                                {/* File Input */}
                                <div className="mb-2">
                                    <input
                                        type="file"
                                        id="fileAttachment"
                                        className="form-control"
                                        multiple
                                        onChange={handleFileSelect}
                                        style={{ borderColor: ARCA_GREEN + '80' }}
                                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                    />
                                    <small className="form-text text-muted">
                                        Maximum 10MB per file. Supported: Images, PDF, Word, Excel, Text
                                    </small>
                                </div>

                                {/* Selected Files List */}
                                {messageData.attachments.length > 0 && (
                                    <div className="mt-2">
                                        <div className="fw-semibold mb-2" style={{ color: ARCA_GREEN }}>
                                            <i className="bi bi-files me-2"></i>
                                            {messageData.attachments.length} file(s) attached
                                        </div>
                                        <div className="list-group">
                                            {messageData.attachments.map((file, index) => (
                                                <div
                                                    key={index}
                                                    className="list-group-item d-flex justify-content-between align-items-center"
                                                    style={{ borderColor: ARCA_GREEN + '40' }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <i className="bi bi-file-earmark me-2" style={{ color: ARCA_GREEN }}></i>
                                                        <div>
                                                            <div className="fw-medium">{file.name}</div>
                                                            <small className="text-muted">
                                                                {(file.size / 1024).toFixed(2)} KB
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeAttachment(index)}
                                                        title="Remove file"
                                                    >
                                                        <i className="bi bi-x-lg"></i>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Info Alert */}
                            {selectedMemberIds.length > 0 && (
                                <div
                                    className="alert border-0 d-flex align-items-start mb-3"
                                    style={{ backgroundColor: ARCA_GREEN + '15', borderLeft: `4px solid ${ARCA_GREEN}` }}
                                >
                                    <i className="bi bi-info-circle me-2 mt-1" style={{ color: ARCA_GREEN }}></i>
                                    <div>
                                        <strong>Ready to send:</strong> Your message will be sent to <strong>{selectedMemberIds.length}</strong> member{selectedMemberIds.length > 1 ? 's' : ''}.
                                        {selectedMemberIds.length > 1 && ' Each member will receive an individual message.'}
                                    </div>
                                </div>
                            )}

                            {/* Send Button */}
                            <div className="d-grid">
                                <button
                                    className="btn btn-lg"
                                    onClick={sendBulkMessage}
                                    disabled={sendingMessage || selectedMemberIds.length === 0 || !messageData.content.trim()}
                                    style={{
                                        backgroundColor: ARCA_GREEN,
                                        borderColor: ARCA_GREEN,
                                        color: 'white'
                                    }}
                                >
                                    {sendingMessage ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Sending to {selectedMemberIds.length} member{selectedMemberIds.length > 1 ? 's' : ''}...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-send-fill me-2"></i>
                                            Send to {selectedMemberIds.length || 0} Member{selectedMemberIds.length !== 1 ? 's' : ''}
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Warning */}
                            {selectedMemberIds.length === 0 && (
                                <div className="alert alert-warning mt-3 mb-0">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    Please select at least one member to send a message
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkMessaging;
